import React, { useEffect, useRef, useState } from 'react';
import * as markerjs2 from 'markerjs2';
import { Box, Flex, useColorModeValue } from '@chakra-ui/react';
import { TriangleMarker } from './TriangleMarker';

interface ImageAnnotatorProps {
    imageUrl: string;
    onSave: (annotatedImageUrl: string, state?: any) => void;
    onCancel: () => void;
    initialState?: any;
}

const ImageAnnotator: React.FC<ImageAnnotatorProps> = ({ imageUrl, onSave, onCancel, initialState }) => {
    const imgRef = useRef<HTMLImageElement>(null);
    const markerAreaRef = useRef<markerjs2.MarkerArea | null>(null);
    const bg = useColorModeValue("white", "gray.800");
    const [crossOrigin, setCrossOrigin] = useState<string | undefined>("anonymous");

    const handleImageLoad = () => {
        if (imgRef.current) {
            // If already initialized for this image, do nothing to avoid double-initialization
            if (markerAreaRef.current) {
                return;
            }

            // Delay initialization to let layout calculations settle and ensure non-zero image size
            setTimeout(() => {
                if (!imgRef.current) return;
                if (markerAreaRef.current) return;

                const markerArea = new markerjs2.MarkerArea(imgRef.current);
                
                // Customize markerjs2 settings for modern feel
                markerArea.settings.displayMode = 'popup';
                markerArea.uiStyleSettings.toolbarBackgroundColor = '#0f172a'; // sleek dark slate
                markerArea.uiStyleSettings.toolbarColor = '#f8fafc';
                markerArea.uiStyleSettings.accentColor = '#10b981';
                markerArea.uiStyleSettings.zIndex = '10000';
                markerArea.settings.defaultColor = '#10b981'; // Emerald Green
                markerArea.settings.defaultFillColor = 'rgba(16, 185, 129, 0.25)'; // Translucent Emerald
                
                // Explicitly define available shapes (including custom TriangleMarker)
                markerArea.availableMarkerTypes = [
                    'FrameMarker',
                    'EllipseMarker',
                    'ArrowMarker',
                    'TextMarker',
                    'LineMarker',
                    'FreehandMarker',
                    'CalloutMarker',
                    TriangleMarker,
                    'HighlightMarker',
                    'CoverMarker'
                ];

                markerArea.addEventListener('render', (event) => {
                    onSave(event.dataUrl, event.state);
                });
                
                markerArea.addEventListener('close', () => {
                    markerAreaRef.current = null;
                    onCancel();
                });

                markerArea.show();
                
                if (initialState) {
                    markerArea.restoreState(initialState);
                }
                
                markerAreaRef.current = markerArea;
            }, 100);
        }
    };

    useEffect(() => {
        if (imgRef.current && imgRef.current.complete) {
            handleImageLoad();
        }
        return () => {
            if (markerAreaRef.current) {
                markerAreaRef.current.close();
                markerAreaRef.current = null;
            }
        };
    }, [imageUrl]);

    return (
        <Flex direction="column" bg={bg} borderRadius="2xl" overflow="hidden" shadow="2xl" w="100%" h="100%">
            <Box 
                p={4} 
                bg="gray.950" 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                h="100%"
                position="relative"
            >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img 
                    ref={imgRef} 
                    src={imageUrl} 
                    alt="Annotate" 
                    onLoad={handleImageLoad}
                    onError={() => {
                        if (crossOrigin === "anonymous") {
                            console.warn("ImageAnnotator: CORS load failed, retrying without crossOrigin");
                            setCrossOrigin(undefined);
                        } else {
                            console.error("ImageAnnotator: Image failed to load even without crossOrigin");
                        }
                    }}
                    style={{ maxWidth: '95%', maxHeight: '76vh', width: 'auto', height: 'auto', objectFit: 'contain', borderRadius: '12px', boxShadow: '0 20px 40px rgba(0,0,0,0.6)' }} 
                    crossOrigin={crossOrigin as any}
                />
            </Box>
        </Flex>
    );
};

export default ImageAnnotator;
